import React, { SetStateAction, useEffect, useState, Dispatch, useRef, useLayoutEffect, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { v1 } from 'uuid';
import { SelectBoxSmDropStyle } from '../assets/styles/SelectBoxSmDrop';
import * as cctv from '../utils/cctv';
import { toast } from 'react-toastify';
import LeftedToast from './cctv/LeftedToast';
import Portal from './Portal';
import styled from 'styled-components';

const OptionList = styled.div`
  position: absolute;
  min-width: 200px;
  max-height: 12rem;
  overflow-y: auto;
  background: var(--board);
  border: 1px solid var(--outline);
  border-radius: 0.25rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  pointer-events: auto;
  z-index: 9999;

  li {
    display: block;
    font-size: 0.875rem;
    flex-shrink: 0;
    width: 100%;
    padding: 0.8rem 0.5rem;
    overflow: visible;
    white-space: break-spaces;
    height: fit-content;
    line-height: 1rem;

    color: var(--text-secondary);
    &:hover:not(li.searchBox, li.selectedOption) {
      color: var(--text-primary);
      background-color: var(--tonal);
    }
  }

  .selectedOption,
  li.selectedOption:hover {
    color: var(--selected-primary);
    background-color: var(--tonal-blue);
    border-radius: 0;
    font-weight: 500;
  }
  .selectColorOption {
    padding: 0.5rem;

    .colorOption {
      width: 100%;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
    }
  }
`;

interface ISelectBox {
  options: any; // 셀렉트박스 배열 [{ subCd: "코드값", cdName:"코드이름" }, ... ]
  defaultOption: string; // 셀렉트 디폴트옵션
  state: any; //  state값
  setState: Dispatch<SetStateAction<any>>; //  useState set액션
  stateKey?: any;
  initiateKey?: any;
  grantChk?: boolean;
  disabled?: boolean;
  getBorderStyle?: any; // 선택값이 없을 때 보더 스타일
  setTableLinesAPI?: (el: any) => void;
  filterbar?: 'filter-1-left' | 'filter-1-leftToCenter' | 'filter-1-center' | 'filter-2-left' | 'filter-2-leftToCenter' | 'filter-2-center';
  optionHeight?: 'height-lg' | 'height-md' | 'height-base' | 'height-sm';
  dropDownWidth?: 'fit-content' | 'expand-content' | 'expand-content-md' | 'expand-content-sm' | 'expand-content-xs';
  align?: 'left' | 'right';
  rsearch?: boolean; // 보기설정으로 사용하는건지 구분
  webSDKClosure?: any;
  pointPosition?: string;
  hideWebSdk?: boolean;
  isWebSdk?: boolean;
  textLabel?: string;
  clickAble?: boolean; // 클릭 시 드롭다운 가능여부 (true: 드롭다운 안됨, false: 드롭다운 됨)
  toastContent?: string; // 드롭다운 안되는 경우 보여줄 토스트 메시지
  toastLocation?: 'center' | 'left';
  optionType?: 'normal' | 'color';
  defaultColor?: { fontColor: string; bgColor: string }; // 컬러 옵션
  childNodeHeight?: number;
  modalRef?: RefObject<HTMLDivElement>;
  setIsUpdated?: (isUpdated: boolean) => void;
}

const SelectBox = ({
  options,
  defaultOption = '',
  state,
  setState,
  stateKey,
  initiateKey,
  grantChk,
  disabled,
  getBorderStyle,
  setTableLinesAPI,
  filterbar,
  dropDownWidth = 'fit-content',
  align = 'left',
  optionHeight = 'height-base',
  rsearch,
  webSDKClosure,
  pointPosition,
  hideWebSdk,
  isWebSdk,
  textLabel,
  clickAble,
  toastContent,
  toastLocation = 'center',
  optionType = 'normal',
  defaultColor,
  childNodeHeight,
  modalRef,
  setIsUpdated,
}: ISelectBox) => {
  const { t } = useTranslation();
  const selRef = useRef<HTMLLIElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({
    width: '80px',
    top: -1000,
    left: -1000,
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>({ type: stateKey, [stateKey]: state[stateKey], cdName: defaultOption });
  const [colorOption, setColorOption] = useState<any>({ fontColor: defaultColor?.fontColor, bgColor: defaultColor?.bgColor });
  const [optionIndex, setOptionIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState<'bottomToTop' | 'topToBottom' | null>(null);

  useLayoutEffect(() => {
    if (initiateKey !== undefined && options !== null && options !== undefined && options.length > 0) {
      // initiate key prop이 변경되었을 때 셀렉트박스 cdName값(선택된옵션)을 다시 세팅
      const findCdName = options.find((el: any) => el[stateKey] === initiateKey || el.cdName === initiateKey);
      if (findCdName !== undefined) {
        setSelectedOption({ type: stateKey, [stateKey]: findCdName[stateKey], cdName: findCdName.cdName });
      } else {
        setSelectedOption({ type: stateKey, [stateKey]: state[stateKey] || '', cdName: t(`${defaultOption}`) || t('미선택') });
      }
    }
  }, [initiateKey, options]);

  useEffect(() => {
    if (initiateKey && optionType === 'color' && initiateKey !== selectedOption[stateKey]) setColorOption({ fontColor: '', bgColor: '' });
  }, [initiateKey]);

  useEffect(() => {
    if (rsearch && selectedOption[stateKey]) {
      setState({ type: stateKey, [stateKey]: selectedOption[stateKey], cdName: selectedOption.cdName });
    } else if (isWebSdk && selectedOption[stateKey] !== undefined) {
      setState({ ...selectedOption, type: stateKey, [stateKey]: selectedOption[stateKey], cdName: selectedOption.cdName });
    } else if (!rsearch && !isWebSdk && selectedOption[stateKey] !== undefined) {
      setState({ type: stateKey, [stateKey]: selectedOption[stateKey], cdName: selectedOption.cdName });
    }
  }, [selectedOption]);

  // 콤보박스 바깥클릭 감지
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (selRef.current && wrapperRef.current) {
        const rect = selRef.current.getBoundingClientRect();
        const wRect = wrapperRef.current.getBoundingClientRect();

        const isOverWidth = rect.left + wRect.width > window.innerWidth;
        const isOverHeight = rect.top + rect.height + wRect.height + 5 > window.innerHeight;

        setDropdownStyle((prev) => ({
          ...prev,
          top: isOverHeight ? rect.bottom - rect.height - wRect.height - 5 : rect.bottom + 5,
          left: isOverWidth ? rect.left + rect.width - wRect.width : rect.left,
          width: rect.width,
          minWidth: 'auto', // OptionList의 min-width: 200px 오버라이드
        }));
      }
    };

    if (isDropdownOpen) {
      // 초기 위치 계산
      updateDropdownPosition();

      // 해상도 변경 이벤트 리스너 추가
      window.addEventListener('resize', updateDropdownPosition);

      // 스크롤 이벤트 리스너 추가
      window.addEventListener('scroll', updateDropdownPosition, true);
    }

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isDropdownOpen]);

  // 콤보박스 열린상태에서 바깥 클릭했을 때 창닫기
  const handleClickOutside = (e: any) => {
    if (wrapperRef && !wrapperRef.current?.contains(e.target)) setIsDropdownOpen(false);
    else setIsDropdownOpen(true);
  };

  // 콤보박스 클릭시 옵션리스트 열기
  const onClickDropdown = () => {
    if (clickAble && toastContent) {
      if (toastLocation === 'left') {
        toast(<LeftedToast message={toastContent} />, { position: 'top-left', autoClose: 3000 });
      } else {
        toast.warn(t(toastContent));
      }
      return;
    }
    if (!grantChk) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  // 콤보박스에서 방향키 위아래로 움직였을 때 옵션이동
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>, option: any) => {
    const key = e.key || e.keyCode;
    if ((key === 'ArrowUp' || key === 38) && optionIndex > 0) {
      //  방향키 위
      setOptionIndex((prev) => prev - 1);
    }
    if ((key === 'ArrowDown' || key === 40) && optionIndex < option.length - 1) {
      //  방향키 아래
      setOptionIndex((prev) => prev + 1);
    }
    if (key === 'Enter' || key === 13) {
      //  엔터
      setSelectedOption(option[optionIndex]);
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  //  메뉴배열 노출
  const renderAllDropdownMenu = (type: 'normal' | 'color') => {
    if (type === 'color') {
      return options?.map((el: any) => (
        <li
          className={`selectColorOption ${stateKey && state[stateKey] === el[stateKey] ? 'selectedOption' : undefined}`}
          key={`${v1()}`}
          role='presentation'
          onClick={() => {
            setColorOption({ fontColor: el.fontColor, bgColor: el.backColor });
            setSelectedOption(el);
            if (setTableLinesAPI) {
              setTableLinesAPI(el);
            }
            setIsDropdownOpen(!isDropdownOpen);
            setIsUpdated?.(true);
          }}
        >
          <div className='colorOption' style={{ backgroundColor: el.backColor, color: el.fontColor }}>
            {t(el.cdName)}
          </div>
        </li>
      ));
    }
    return options?.map((el: any) => (
      <li
        className={stateKey && state[stateKey] === el[stateKey] ? 'selectedOption' : undefined}
        key={`${v1()}`}
        role='presentation'
        onClick={() => {
          setSelectedOption(el);
          if (setTableLinesAPI) {
            setTableLinesAPI(el);
          }
          setIsDropdownOpen(!isDropdownOpen);
          setIsUpdated?.(true);
        }}
      >
        {t(el.cdName)}
      </li>
    ));
  };

  const emptyData = !options || options?.length === 0;

  const renderCdName = () => {
    if (emptyData) return `${t('목록 없음')}`;
    if (selectedOption.cdName) return t(selectedOption.cdName);
    return t(defaultOption);
  };

  useLayoutEffect(() => {
    /*
    const updatePosition = () => {
      let rect;
      const dropdownWrapperRect = wrapperRef?.current?.getBoundingClientRect();

      if (modalRef) rect = modalRef?.current?.getBoundingClientRect();
      else rect = wrapperRef?.current?.getBoundingClientRect();

      if (rect && dropdownWrapperRect) {
        const inHeight = modalRef ? rect.height : window.innerHeight;
        if (
          (childNodeHeight && childNodeHeight < rect.bottom && rect.top - 72 > rect.height) ||
          (inHeight - 72 < dropdownWrapperRect.bottom && dropdownWrapperRect.top - 72 > dropdownWrapperRect.height)
        ) {
          setDropdownPosition('bottomToTop');
        } else {
          setDropdownPosition('topToBottom');
        }
      }
    };
    updatePosition();
    */

    if (cctv.getPluginOBJECT()?.oPlugin && hideWebSdk) {
      if (isDropdownOpen) {
        cctv.hideWndAll();
      } else {
        cctv.showWndAll();
      }
    }
    if (pointPosition && webSDKClosure?.getCctvOBJ(pointPosition)) {
      const hide = () => webSDKClosure.getCctvOBJ(pointPosition)?.hideWnd();
      const show = () => webSDKClosure.getCctvOBJ(pointPosition)?.showWnd();
      isDropdownOpen ? hide() : show();
    }
  }, [isDropdownOpen]);

  return (
    <SelectBoxSmDropStyle isDropdownOpen={isDropdownOpen} className={textLabel ? 'widthLabel' : ''}>
      {textLabel && <label htmlFor='tatGubun'>{t(textLabel)}</label>}
      <ul>
        <li
          className={`defaultOption ${disabled ? 'disabled' : ''} ${emptyData ? 'disabled' : ''} ${isDropdownOpen ? 'opened' : ''}`}
          ref={selRef}
          role='presentation'
          onClick={onClickDropdown}
          style={getBorderStyle}
        >
          <span style={optionType === 'color' ? { color: colorOption.fontColor, backgroundColor: colorOption.bgColor, borderRadius: '3px' } : undefined}>{renderCdName()}</span>
          <div>
            <span className='material-symbols-rounded'>keyboard_arrow_down</span>
          </div>
        </li>
        {/* {!disabled ? (
          <div className={`optionBox ${isDropdownOpen && options?.length > 0 ? 'on' : 'off'} ${filterbar} ${optionHeight} ${dropDownWidth} ${dropdownPosition} ${align}`} ref={wrapperRef}>
            {renderAllDropdownMenu(optionType)}
          </div>
        ) : null} */}
        {isDropdownOpen && options?.length > 0 && (
          <Portal openModal>
            <OptionList ref={wrapperRef} style={dropdownStyle}>
              {renderAllDropdownMenu(optionType)}
            </OptionList>
          </Portal>
        )}
      </ul>
      {/* <div className={` ${isDropdownOpen && 'optionBox-filter'}`} /> */}
    </SelectBoxSmDropStyle>
  );
};

export default React.memo(SelectBox);
